'use client'

import { type RefObject, useLayoutEffect, useState } from 'react'

export interface UseToolbarFitsInlineOptions {
    containerRef: RefObject<HTMLElement | null>
    measureLeftRef: RefObject<HTMLElement | null>
    measureMiddleRef?: RefObject<HTMLElement | null>
    measureRightRef: RefObject<HTMLElement | null>
    sectionGap?: number
    enabled?: boolean
    deps?: unknown[]
}

export function useToolbarFitsInline({
    containerRef,
    measureLeftRef,
    measureMiddleRef,
    measureRightRef,
    sectionGap = 16,
    enabled = true,
    deps = [],
}: UseToolbarFitsInlineOptions) {
    const [fitsInline, setFitsInline] = useState(true)

    useLayoutEffect(() => {
        if (!enabled) {
            setFitsInline(true)
            return
        }

        const measure = () => {
            const container = containerRef.current
            const left = measureLeftRef.current
            const middle = measureMiddleRef?.current
            const right = measureRightRef.current

            if (!container || !left || !right) {
                return
            }

            const middleWidth = middle?.scrollWidth ?? 0
            const gaps = middle ? sectionGap * 2 : sectionGap
            const requiredWidth = left.scrollWidth + middleWidth + right.scrollWidth + gaps
            setFitsInline(requiredWidth <= container.clientWidth)
        }

        measure()

        const observer = new ResizeObserver(measure)
        const elements = [containerRef.current, measureLeftRef.current, measureMiddleRef?.current ?? null, measureRightRef.current]

        elements.forEach((element) => {
            if (element) {
                observer.observe(element)
            }
        })

        return () => observer.disconnect()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are caller-controlled remeasure triggers
    }, [containerRef, measureLeftRef, measureMiddleRef, measureRightRef, sectionGap, enabled, ...deps])

    return fitsInline
}
